import React, { useEffect, useState } from "react"
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete"
import { Alert, Button, FormGroup, FormText, Input, Label, ModalBody } from "reactstrap"
import { get } from "helpers/api_helper"
import { EMAIL, addressFromGoogle, blankAddress, countryCode, courierPhone, usedAtLabel } from "./delivery"
import DraftModal from "./DraftModal"
import { ReasonField } from "./AfterShipmentDialogs"
import { shipmentName } from "./shipmentLabels"
import { withFloatingSurface } from "components/Common/selectSurface"

// What a correction after shipment keeps (US 86): the main shipment's address and contact are the sheet's, a supplementary shipment's
// its own.
const keptBy = shipment => (shipment ? "The supplementary shipment keeps the corrected copy; the sheet keeps its own."
  : "The shipment and the sheet keep the corrected copy.")
// The dialog's title for the sheet, or for a supplementary shipment.
const titled = (title, shipment) => (shipment ? `${title} — ${shipmentName(shipment)}` : title)

const fieldsOf = address => Object.fromEntries(Object.keys(blankAddress).map(field => [field, address?.[field] || ""]))
const trimmed = fields => Object.fromEntries(Object.entries(fields).map(([field, value]) => [field, value.trim()]))
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right)

// What the address and contact selectors offer (Events delivery options), loaded once per dialog.
function useDeliveryOptions(eventId) {
  const [state, setState] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let active = true
    setState({ loading: true })
    get(`/admin/logistics/events/${eventId}/sheet/delivery-options`)
      .then(response => { if (active) setState({ data: response.data }) })
      .catch(() => { if (active) setState({ error: true }) })
    return () => { active = false }
  }, [eventId, attempt])
  return { ...state, retry: () => setAttempt(value => value + 1) }
}

// A dialog of the delivery, once its options are loaded; its draft is kept until the administrator discards it (DraftModal).
function DeliveryDialog({ id, title, options, dirty, failure, onClose, actions, children }) {
  return <DraftModal id={id} title={title} size="lg" dirty={dirty} onClose={onClose} actions={options.data && actions}>
    <ModalBody>
      {options.loading && <p role="status">Loading the options…</p>}
      {options.error && <Alert color="danger">The options could not be loaded. <Button color="link" className="p-0 align-baseline" onClick={options.retry}>Retry</Button></Alert>}
      {options.data && children}
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
  </DraftModal>
}

function Field({ id, label, required, hint, className = "col-sm-6", children }) {
  return <FormGroup className={className}>
    <Label for={id}>{label}{required && <span className="text-danger"> *</span>}</Label>
    {children}
    {hint && <FormText>{hint}</FormText>}
  </FormGroup>
}

// "Event club — CS Olimpia Brașov (last confirmed, used at Silver Brașov Winter Cup, 02 Mar 2026)",
// "Previous address of the organizer — Popa Sport SRL, Brașov (used at Silver Poiana, 10 May 2026)".
const addressOptionLabel = ({ kind, scope, club, address, usedAt }, timezone) => {
  if (kind === "previous") return `Previous address ${scope === "club" ? "at this club" : "of the organizer"} — `
    + `${[address.recipient, address.locality].filter(Boolean).join(", ")} (used at ${usedAtLabel(usedAt, timezone)})`
  return `${kind === "eventClub" ? "Event club" : "Other club of the organizer"} — ${club.name} `
    + `(${address ? `last confirmed, used at ${usedAtLabel(usedAt, timezone)}` : "public address, to confirm"})`
}

// Where the fields start from: the current address; a club's last confirmed delivery address or,
// without one, its public address; an address used before, an alternative; or none, entered by hand
// in the club's country.
const chosenAddress = (choice, { eventId, current, country }) => {
  if (choice === "current") return { fields: fieldsOf(current), club: current?.club?.id || null, from: current?.confirmed ? eventId : null }
  if (choice === "manual") return { fields: { ...blankAddress, country }, club: null, from: null }
  if (choice.kind === "previous") return { fields: fieldsOf(choice.address), club: null, from: choice.from }
  return { fields: fieldsOf(choice.address || choice.prefill), club: choice.club.id, from: choice.address ? choice.from : null }
}


// The sheet's shipping address. Confirming it saves a copy on the sheet; chosen as a club's (the
// event's, another of its organizer's, or the one it already belongs to), it becomes that club's last
// confirmed delivery address. Google prefills it here only, in CPanel: the county aligned to the list,
// a region outside Romania. Once shipped (`correction`), it is the main shipment's address, corrected
// with an optional reason (T5, Tudor 2026-10-06): it stays the address of the club it belonged to. A supplementary shipment's address
// (`shipment`) starts from the same options, saved on that shipment only: in preparation without a reason, once shipped as a correction
// with an optional one. In preparation it is the club's — the event's or the one its copy belongs to — only when the SuperAdmin says so
// (US 101); an alternative address never becomes a club's delivery address.
export function AddressDialog({ eventId, timezone, current, place, shipment = null, correction = false, pending, failure, onSubmit, onClose }) {
  const options = useDeliveryOptions(eventId)
  const addresses = options.data?.addresses || []
  const clubOptions = addresses.filter(option => option.club && (!shipment || option.kind === "eventClub"))
  const country = current?.country || countryCode(place?.country)
  const [initial] = useState(() => chosenAddress(current ? "current" : "manual", { eventId, current, country }))
  const [choice, setChoice] = useState(current ? "current" : "manual")
  const [draft, setDraft] = useState(initial)
  const [google, setGoogle] = useState({})
  const { fields, club, from } = draft
  // The clubs an address can belong to, and the place of the chosen one for its Google prefill.
  const clubs = [...clubOptions.map(option => option.club), ...(current?.club && !clubOptions.some(option => option.club.id === current.club.id) ? [current.club] : [])]
  const clubPlace = clubOptions.find(option => option.club.id === club)?.place
  const set = (field, value) => setDraft(previous => ({ ...previous, fields: { ...previous.fields, [field]: value,
    ...(field === "country" ? { county: "", region: "" } : {}) } }))
  const belongTo = value => setDraft(previous => ({ ...previous, club: value || null }))
  const choose = value => {
    setChoice(value)
    setGoogle({})
    const chosen = chosenAddress(["current", "manual"].includes(value) ? value : addresses[Number(value)], { eventId, current, country })
    // Another club of the organizer is not one a supplementary shipment's address can belong to: it starts as an alternative address.
    setDraft(clubs.some(option => option.id === chosen.club) ? chosen : { ...chosen, club: null })
  }
  const prefill = async placeId => {
    setGoogle({ loading: true })
    try {
      const [result] = await geocodeByPlaceId(placeId)
      const found = addressFromGoogle(result, options.data.counties)
      setDraft(previous => ({ ...previous, fields: { ...previous.fields, ...found } }))
      setGoogle({})
    } catch (error) {
      setGoogle({ error: error.message?.startsWith("Choose") ? error.message : "Google could not prefill this address. Try again." })
    }
  }
  const romanian = fields.country === "RO"
  const [reason, setReason] = useState("")
  const confirm = () => onSubmit(correction ? { action: "correctDetails", address: trimmed(fields), reason: reason.trim() }
    : shipment ? { action: "setSupplementaryDelivery", address: trimmed(fields), club }
      : { action: "setAddress", address: trimmed(fields), club, confirm: true, ...(from ? { from } : {}) })
  const unchanged = same(trimmed(fields), trimmed(initial.fields))

  return <DeliveryDialog id="logistics-address-title" title={titled(correction ? "Correct the delivery address" : "Delivery address", shipment)}
    options={options}
    dirty={!same(draft, initial) || Boolean(reason.trim())} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending || google.loading || (correction && unchanged)} onClick={confirm}>
      {pending ? "Saving…" : correction ? "Save correction" : "Confirm address"}
    </Button>}>
    {!correction && <div className="form-row">
      <Field id="logistics-address-source" label="Source" className="col-sm-7">
        <Input id="logistics-address-source" type="select" value={choice} onChange={event => choose(event.target.value)}>
          {current && <option value="current">{shipment ? "Current address of this shipment" : "Current address on this sheet"}</option>}
          {addresses.map((option, index) => <option key={index} value={String(index)}>{addressOptionLabel(option, timezone)}</option>)}
          <option value="manual">Manual</option>
        </Input>
      </Field>
      <Field id="logistics-address-club" label="Delivery address of" className="col-sm-5">
        <Input id="logistics-address-club" type="select" value={club || ""} onChange={event => belongTo(event.target.value)}>
          {clubs.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          <option value="">No club: an alternative address</option>
        </Input>
      </Field>
    </div>}
    <div className="d-flex flex-wrap align-items-start mb-3">
      <div className="flex-grow-1 mr-2 mb-2" style={{ minWidth: 240 }}>
        <GooglePlacesAutocomplete apiKey={process.env.REACT_APP_GOOGLE_MAPS_API} apiOptions={{ language: "en" }} minLengthAutocomplete={2}
          onLoadFailed={() => setGoogle({ error: "Google could not be loaded. Enter the address by hand." })}
          autocompletionRequest={{ componentRestrictions: { country: ["ro", "bg", "md"] } }}
          selectProps={{ styles: withFloatingSurface(),  inputId: "logistics-address-search", "aria-label": "Find on Google", value: null, isDisabled: google.loading,
            onChange: option => option && prefill(option.value.place_id), placeholder: "Find on Google: street, venue…",
            noOptionsMessage: () => "Type the street or venue" }} />
      </div>
      {clubPlace?.placeId && <Button color="light" className="mb-2" disabled={google.loading} onClick={() => prefill(clubPlace.placeId)}>Prefill from Google (club place)</Button>}
    </div>
    {google.loading && <p role="status" className="small">Prefilling from Google…</p>}
    {google.error && <Alert color="warning" className="py-2">{google.error}</Alert>}
    <div className="form-row">
      <Field id="logistics-address-recipient" label="Recipient" required className="col-12" hint="The club, the organizer's company or the person, as the AWB names it.">
        <Input id="logistics-address-recipient" maxLength={200} value={fields.recipient} onChange={event => set("recipient", event.target.value)} />
      </Field>
      <Field id="logistics-address-country" label="Country" required className="col-sm-4">
        <Input id="logistics-address-country" type="select" value={fields.country} onChange={event => set("country", event.target.value)}>
          <option value="">Choose a country</option>
          {options.data?.countries.map(option => <option key={option.code} value={option.code}>{option.name}</option>)}
        </Input>
      </Field>
      {romanian || !fields.country ? <Field id="logistics-address-county" label="County" required className="col-sm-8">
        <Input id="logistics-address-county" type="select" value={fields.county} disabled={!romanian} onChange={event => set("county", event.target.value)}>
          <option value="">{romanian ? "Choose a county" : "Choose the country first"}</option>
          {romanian && options.data?.counties.map(county => <option key={county} value={county}>{county}</option>)}
        </Input>
      </Field> : <Field id="logistics-address-region" label="Region" className="col-sm-8" hint="Optional.">
        <Input id="logistics-address-region" maxLength={200} value={fields.region} onChange={event => set("region", event.target.value)} />
      </Field>}
      <Field id="logistics-address-locality" label="Locality" required>
        <Input id="logistics-address-locality" maxLength={200} value={fields.locality} onChange={event => set("locality", event.target.value)} />
      </Field>
      <Field id="logistics-address-street" label="Street" required>
        <Input id="logistics-address-street" maxLength={200} value={fields.street} onChange={event => set("street", event.target.value)} />
      </Field>
      <Field id="logistics-address-number" label="Number" required className="col-sm-4" hint="Or “f.n.”">
        <Input id="logistics-address-number" maxLength={200} value={fields.number} onChange={event => set("number", event.target.value)} />
      </Field>
      <Field id="logistics-address-postal" label="Postal code" required={Boolean(fields.country) && !romanian} className="col-sm-4" hint={romanian ? "Recommended." : null}>
        <Input id="logistics-address-postal" maxLength={200} value={fields.postalCode} onChange={event => set("postalCode", event.target.value)} />
      </Field>
      <Field id="logistics-address-details" label="Details" className="col-sm-4" hint="Building, landmark.">
        <Input id="logistics-address-details" maxLength={200} value={fields.details} onChange={event => set("details", event.target.value)} />
      </Field>
    </div>
    {fields.country && !romanian && <p className="small text-muted">Outside Romania the postal code is required and the AWB is always entered by hand.</p>}
    {correction ? <ReasonField id="logistics-address-reason" optional value={reason} onChange={setReason} placeholder="e.g. a typo in the street"
      hint={keptBy(shipment)} />
      : <p className="small text-muted mb-0">{shipment
        ? `Saved on this supplementary shipment; the sheet keeps its address. ${club
          ? "As the club's address, it becomes the club's last confirmed delivery address for future sheets."
          : "As an alternative address, it never becomes a club's delivery address."}`
        : club ? "Confirming it makes it the club's last confirmed delivery address for future sheets."
          : "Saved on this sheet only: an alternative address never becomes a club's delivery address."}</p>}
  </DeliveryDialog>
}

// "Previous contact at this club — Mihai Stan (used at Silver Brașov Winter Cup, 02 Mar 2026)".
const contactOptionLabel = ({ kind, scope, contact, usedAt }, timezone) => ({
  organizer: `Organizer — ${contact.name} (event phone and email)`,
  club: `Club public contact — ${contact.name || contact.phone || contact.email}`,
}[kind] || `Previous contact ${scope === "club" ? "at this club" : "of the organizer"} — ${contact.name} (used at ${usedAtLabel(usedAt, timezone)})`)
const personOf = contact => ({ name: contact?.name || "", phone: contact?.phone || "", email: contact?.email || "" })
const samePerson = (left, right) => ["name", "phone", "email"].every(field => left[field].trim() === right[field])

// The sheet's contact person: saved on the sheet only, never written back to a profile. A phone or an
// email is free text, as on the profiles it comes from; what a courier cannot use stays to complete.
// Outside Romania a courier needs the phone's country prefix; a Romanian 07… number gets +40. Once
// shipped (`correction`), it is the main shipment's contact, corrected with an optional reason (T5, Tudor 2026-10-06). A
// supplementary shipment's contact (`shipment`) is saved on that shipment only, in preparation without a reason.
export function ContactDialog({ eventId, timezone, current, country, shipment = null, correction = false, pending, failure, onSubmit, onClose }) {
  const options = useDeliveryOptions(eventId)
  const contacts = options.data?.contacts || []
  const [choice, setChoice] = useState(current ? "current" : "manual")
  const [initial] = useState(() => personOf(current))
  const [person, setPerson] = useState(initial)
  const option = choice === "current" || choice === "manual" ? null : contacts[Number(choice)]
  const choose = value => {
    setChoice(value)
    setPerson(personOf(value === "current" ? current : value === "manual" ? null : contacts[Number(value)].contact))
  }
  const set = (field, value) => setPerson(previous => ({ ...previous, [field]: value }))
  const abroad = country && country !== "RO"
  const unusableEmail = person.email.trim() && !EMAIL.test(person.email.trim())
  // The source is the option's while its values are kept; changed, the contact is entered by hand.
  const source = option && samePerson(person, option.contact) ? option.kind
    : choice === "current" && current && samePerson(person, personOf(current)) ? current.source.kind : "manual"
  const [reason, setReason] = useState("")
  const entered = { name: person.name.trim(), phone: person.phone.trim(), email: person.email.trim() }
  const save = () => onSubmit(correction ? { action: "correctDetails", contact: entered, reason: reason.trim() }
    : shipment ? { action: "setSupplementaryDelivery", contact: entered } : { action: "setContact", contact: entered, source })

  return <DeliveryDialog id="logistics-contact-title" title={titled(correction ? "Correct the contact person" : "Contact person", shipment)}
    options={options}
    dirty={!same(person, initial) || Boolean(reason.trim())} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending || (correction && samePerson(person, initial))} onClick={save}>
      {pending ? "Saving…" : correction ? "Save correction" : "Save"}
    </Button>}>
    {!correction && <Field id="logistics-contact-source" label="Source" className="">
      <Input id="logistics-contact-source" type="select" value={choice} onChange={event => choose(event.target.value)}>
        {current && <option value="current">{shipment ? "Current contact of this shipment" : "Current contact on this sheet"}</option>}
        {contacts.map((entry, index) => <option key={index} value={String(index)}>{contactOptionLabel(entry, timezone)}</option>)}
        <option value="manual">Manual</option>
      </Input>
    </Field>}
    <div className="form-row">
      <Field id="logistics-contact-name" label="Name" required>
        <Input id="logistics-contact-name" maxLength={200} value={person.name} onChange={event => set("name", event.target.value)} />
      </Field>
      <Field id="logistics-contact-phone" label="Phone" required hint={abroad ? "With the country prefix, e.g. +359 …; a Romanian 07… number becomes +40 7…" : null}>
        <Input id="logistics-contact-phone" maxLength={200} value={person.phone} placeholder={abroad ? "+359 …" : "+40 7xx xxx xxx"}
          onChange={event => set("phone", event.target.value)} onBlur={() => set("phone", courierPhone(person.phone, country))} />
      </Field>
      <Field id="logistics-contact-email" label="Email" required className="col-12"
        hint={unusableEmail ? "A courier cannot use this email: the contact stays to complete." : null}>
        <Input id="logistics-contact-email" type="email" maxLength={200} value={person.email} onChange={event => set("email", event.target.value)} />
      </Field>
    </div>
    <p className={`small text-muted${correction ? "" : " mb-0"}`}>Required with a courier: name, phone and email; without one, name and phone.
      {shipment ? " Saved on this supplementary shipment only" : " Saved on this sheet only"} — never written back to the organizer's or the club's profile.</p>
    {correction && <ReasonField id="logistics-contact-reason" optional value={reason} onChange={setReason} placeholder="e.g. another person receives it"
      hint={keptBy(shipment)} />}
  </DeliveryDialog>
}
